import { Globe, ArrowRight, Zap } from "lucide-react";
import Link from "next/link";
import { z } from "zod";

const StatSchema = z.object({
	value: z.string(),
	label: z.string(),
	color: z.string(),
});

const CityAvatarSchema = z.object({
	code: z.string(),
	name: z.string(),
	position: z.string(),
	size: z.string(),
	color: z.string(),
	animationDelay: z.string().optional(),
});

const InfoCardSchema = z.object({
	value: z.string(),
	label: z.string(),
	color: z.string(),
	position: z.string(),
});

const HeroSchema = z.object({
	badgeText: z.string().default("Exclusive Community"),
	header: z.string().default("Join the Global"),
	highlightText: z.string().default("PropTech Founders/CEOs Circle"),
	description: z
		.string()
		.default(
			"Connect with peers in the world’s largest Proptech founders/CEOs community. Exchange insights, forge partnerships, and expand your opportunities in this exclusive community"
		),
	primaryButtonText: z.string().default("Join the Founders Circle"),
	secondaryButtonText: z.string().default("Learn More"),
	stats: z.array(StatSchema).default([
		{ value: "500+ Active Founders", label: "", color: "text-blue-200" },
		{ value: "65+ Countries", label: "", color: "text-blue-200" },
		{ value: "$4.2B+ Combined Funding", label: "", color: "text-blue-200" },
	]),
	majorCities: z.array(CityAvatarSchema).default([
		{
			code: "SF",
			name: "San Francisco",
			position: "-top-6 left-1/2 -translate-x-1/2 transform",
			size: "h-12 w-12",
			color: "bg-orange-500",
		},
		{
			code: "LD",
			name: "London",
			position: "-right-10 top-12",
			size: "h-10 w-10",
			color: "bg-teal-500",
		},
		{
			code: "NY",
			name: "New York",
			position: "-left-14 top-1/2 -translate-y-1/2 transform",
			size: "h-11 w-11",
			color: "bg-blue-500",
		},
		{
			code: "SG",
			name: "Singapore",
			position: "bottom-16 right-12",
			size: "h-10 w-10",
			color: "bg-green-500",
		},
		{
			code: "BR",
			name: "Berlin",
			position: "-left-8 bottom-8",
			size: "h-9 w-9",
			color: "bg-purple-500",
		},
		{
			code: "SY",
			name: "Sydney",
			position: "-bottom-6 right-24",
			size: "h-10 w-10",
			color: "bg-pink-500",
		},
	]),
	secondaryCities: z.array(CityAvatarSchema).default([
		{
			code: "TK",
			name: "Tokyo",
			position: "left-8 top-8",
			size: "h-8 w-8",
			color: "bg-red-500",
			animationDelay: "2s",
		},
		{
			code: "TO",
			name: "Toronto",
			position: "left-16 top-20",
			size: "h-7 w-7",
			color: "bg-indigo-500",
			animationDelay: "3s",
		},
		{
			code: "DB",
			name: "Dubai",
			position: "right-4 top-1/3",
			size: "h-7 w-7",
			color: "bg-amber-500",
			animationDelay: "2.5s",
		},
		{
			code: "AM",
			name: "Amsterdam",
			position: "left-6 top-1/4",
			size: "h-7 w-7",
			color: "bg-cyan-500",
			animationDelay: "4s",
		},
		{
			code: "BC",
			name: "Barcelona",
			position: "bottom-1/3 left-12",
			size: "h-7 w-7",
			color: "bg-rose-500",
			animationDelay: "1s",
		},
		{
			code: "SP",
			name: "São Paulo",
			position: "bottom-6 left-8",
			size: "h-7 w-7",
			color: "bg-emerald-500",
			animationDelay: "3.5s",
		},
		{
			code: "MB",
			name: "Mumbai",
			position: "bottom-1/4 right-8",
			size: "h-7 w-7",
			color: "bg-violet-500",
			animationDelay: "0.5s",
		},
		{
			code: "ST",
			name: "Stockholm",
			position: "left-20 top-10",
			size: "h-6 w-6",
			color: "bg-lime-500",
			animationDelay: "5s",
		},
		{
			code: "MC",
			name: "Mexico City",
			position: "bottom-10 right-6",
			size: "h-6 w-6",
			color: "bg-orange-400",
			animationDelay: "4.5s",
		},
	]),
	infoCards: z.array(InfoCardSchema).default([
		{
			value: "750+",
			label: "Active Founders",
			color: "text-orange-400",
			position: "-bottom-12 left-0",
		},
		{
			value: "65+",
			label: "Countries",
			color: "text-teal-400",
			position: "-top-12 right-0",
		},
		{
			value: "$4.2B+",
			label: "Total Funding",
			color: "text-green-400",
			position: "-left-16 top-1/2 -translate-y-1/2 transform",
		},
		{
			value: "150+",
			label: "Cities",
			color: "text-purple-400",
			position: "-right-16 top-1/2 -translate-y-1/2 transform",
		},
	]),
});

type HeroProps = z.input<typeof HeroSchema>;

const Hero: React.FC<HeroProps> = (props = {}) => {
	const data = HeroSchema.parse(props);

	return (
		<section className="relative flex min-h-screen items-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-teal-800">
			{/* Background Elements */}
			<div className="absolute inset-0">
				<div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl"></div>
				<div className="absolute bottom-20 right-10 h-96 w-96 rounded-full bg-teal-500/10 blur-3xl"></div>
				<div className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-blue-500/5 blur-3xl"></div>
			</div>

			<div className="relative z-10 mx-auto max-w-7xl px-4 py-32 sm:px-6 lg:px-8">
				<div className="grid items-center gap-12 lg:grid-cols-2">
					<div className="text-white">
						<div className="mb-6">
							<span className="inline-flex items-center rounded-full border border-orange-400/30 bg-orange-600/20 px-4 py-2 text-sm font-medium text-orange-300 backdrop-blur-sm">
								<Zap className="mr-2 h-4 w-4" />
								{data.badgeText}
							</span>
						</div>

						<h1 className="mb-6 text-4xl font-bold leading-tight md:text-6xl">
							{data.header}
							<span className="block bg-gradient-to-r from-teal-400 to-orange-400 bg-clip-text text-transparent">
								{data.highlightText}
							</span>
						</h1>

						<p className="mb-8 text-xl leading-relaxed text-blue-100 md:text-2xl">
							{data.description}
						</p>

						<div className="mb-8 flex flex-col gap-4 sm:flex-row">
							<Link href={`https://www.proptechbuzz.com/communities/global-proptech-founders`}>
								<button className="group transform rounded-full bg-orange-600 px-8 py-4 text-lg font-semibold text-white transition-all duration-200 hover:-translate-y-1 hover:bg-orange-700 hover:shadow-2xl hover:shadow-orange-600/25">
									{data.primaryButtonText}
									<ArrowRight className="ml-2 inline-block h-5 w-5 transition-transform group-hover:translate-x-1" />
								</button>
							</Link>
							<button className="rounded-full border-2 border-white/30 px-8 py-4 text-lg font-semibold text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/10">
								{data.secondaryButtonText}
							</button>
						</div>

						<div className="flex items-center space-x-8 text-sm text-blue-200">
							{data.stats.map((stat, index) => (
								<div key={index} className="flex items-center">
									<div className="mr-2 h-2 w-2 rounded-full bg-green-400"></div>
									{stat.value}
								</div>
							))}
						</div>
					</div>

					<div className="relative flex items-center justify-center">
						{/* Global PropTech Founders Globe Visualization */}
						<div className="relative h-96 w-96">
							{/* Main Globe */}
							<div className="absolute inset-0 rounded-full border-2 border-white/20 bg-gradient-to-br from-blue-400/30 to-teal-400/30 backdrop-blur-sm">
								{/* Globe Grid Lines */}
								<div className="absolute inset-4 rounded-full border border-white/10"></div>
								<div className="absolute inset-8 rounded-full border border-white/10"></div>
								<div className="absolute inset-12 rounded-full border border-white/5"></div>
								<div className="absolute left-0 right-0 top-1/2 h-px bg-white/10"></div>
								<div className="absolute bottom-0 left-1/2 top-0 w-px bg-white/10"></div>
								<div className="absolute left-0 right-0 top-1/4 h-px rotate-12 transform bg-white/5"></div>
								<div className="absolute left-0 right-0 top-3/4 h-px -rotate-12 transform bg-white/5"></div>

								{/* Central Globe Icon */}
								<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
									<Globe className="h-20 w-20 text-teal-300" />
								</div>
							</div>

							{/* Floating Founder Avatars - Major Hubs */}
							{data.majorCities.map((city, index) => (
								<div key={index} className={`absolute ${city.position} animate-float`}>
									<div
										className={`flex ${city.size} items-center justify-center rounded-full border-2 border-white/30 ${city.color} text-sm font-bold text-white`}
									>
										{city.code}
									</div>
									<div className="absolute -bottom-6 left-1/2 -translate-x-1/2 transform whitespace-nowrap text-xs text-white/80">
										{city.name}
									</div>
								</div>
							))}

							{/* Additional Founder Avatars - Secondary Hubs */}
							{data.secondaryCities.map((city, index) => (
								<div
									key={index}
									className={`absolute ${city.position} animate-float`}
									style={city.animationDelay ? { animationDelay: city.animationDelay } : {}}
								>
									<div
										className={`flex ${city.size} items-center justify-center rounded-full border-2 border-white/30 ${city.color} text-xs font-bold text-white`}
									>
										{city.code}
									</div>
								</div>
							))}

							{/* Connection Lines */}
							<div className="absolute inset-0">
								<svg className="h-full w-full opacity-30">
									<defs>
										<linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
											<stop offset="0%" stopColor="#ffffff" stopOpacity="0.3" />
											<stop offset="100%" stopColor="#ffffff" stopOpacity="0.1" />
										</linearGradient>
										<linearGradient id="lineGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
											<stop offset="0%" stopColor="#22d3ee" stopOpacity="0.3" />
											<stop offset="100%" stopColor="#f97316" stopOpacity="0.2" />
										</linearGradient>
									</defs>
									<path
										d="M 192 24 Q 240 120 288 144"
										stroke="url(#lineGradient)"
										strokeWidth="1"
										fill="none"
										className="animate-pulse"
									/>
									<path
										d="M 48 192 Q 120 168 192 192"
										stroke="url(#lineGradient)"
										strokeWidth="1"
										fill="none"
										className="animate-pulse"
									/>
									<path
										d="M 336 216 Q 264 240 216 288"
										stroke="url(#lineGradient)"
										strokeWidth="1"
										fill="none"
										className="animate-pulse"
									/>
									<path
										d="M 80 80 Q 150 120 220 100"
										stroke="url(#lineGradient2)"
										strokeWidth="1"
										fill="none"
										className="animate-pulse"
										style={{ animationDelay: "2s" }}
									/>
									<path
										d="M 300 100 Q 250 150 200 200"
										stroke="url(#lineGradient2)"
										strokeWidth="1"
										fill="none"
										className="animate-pulse"
										style={{ animationDelay: "3s" }}
									/>
									<path
										d="M 100 280 Q 180 260 260 270"
										stroke="url(#lineGradient)"
										strokeWidth="1"
										fill="none"
										className="animate-pulse"
										style={{ animationDelay: "1s" }}
									/>
								</svg>
							</div>

							{/* Pulse Rings */}
							<div className="absolute inset-0 animate-ping rounded-full border border-orange-400/20"></div>
							<div
								className="absolute inset-4 animate-ping rounded-full border border-teal-400/20"
								style={{ animationDelay: "1s" }}
							></div>
							<div
								className="border-blue-400/15 absolute inset-8 animate-ping rounded-full border"
								style={{ animationDelay: "2s" }}
							></div>
						</div>

						{/* Info Cards */}
						{data.infoCards.map((card, index) => (
							<div
								key={index}
								className={`absolute ${card.position} rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-md`}
							>
								<div className="text-center">
									<div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
									<div className="text-xs text-white/80">{card.label}</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			<style jsx>{`
				@keyframes float {
					0%,
					100% {
						transform: translateY(0px);
					}
					50% {
						transform: translateY(-20px);
					}
				}
				@keyframes float-delayed {
					0%,
					100% {
						transform: translateY(0px);
					}
					50% {
						transform: translateY(-15px);
					}
				}
				.animate-float {
					animation: float 6s ease-in-out infinite;
				}
				.animate-float-delayed {
					animation: float-delayed 8s ease-in-out infinite;
				}
			`}</style>
		</section>
	);
};

export default Hero;
